import {environment} from "$lib/env";
import type {IBalance} from "$lib/interfaces/IBalance";
import {getLocalStorage, setLocalStorage} from "$lib/utils/localStorage";
import type {IUserMe} from "$lib/interfaces/IUserMe";
import type {ILevelResponse} from "$lib/interfaces/ILevel";
import type {IProvider} from "$lib/interfaces/IProvider";
import type {IBankTransferResponse} from "$lib/interfaces/IBankTransferResponse";
import type {IBonusStats} from "$lib/interfaces/IBonusStats";
import type {IUserKyc} from "$lib/interfaces/IUserKyc";
import type {IDepositOffer} from "$lib/interfaces/IDepositOffer";
import type {IGamblingLimits} from "$lib/interfaces/IGamblingLimits";
import type {ISessions} from "$lib/interfaces/ISessions";
import type {ITotpCode} from "$lib/interfaces/ITotpCode";
import { formatDateForApi } from "$lib/utils/apiDateFormat";
import { authStore } from "$lib/stores/authStore";

interface ApiResponse<T> {
  data: T;
  error?: string;
}

class UserService {
  private readonly apiUrl: string = environment.apiUrl;

  constructor() {}

  private async refreshToken(): Promise<boolean> {
    try {
      // Get refresh token from the auth store
      const refreshToken = authStore.getRefreshToken();

      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${this.apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) {
        return false;
      }

      const { data } = await response.json();

      // Update tokens in the auth store
      authStore.setAuth({
        accessToken: data.tokens.accessToken,
        refreshToken: data.tokens.refreshToken,
        accessTokenExpiresIn: data.tokens.accessTokenExpiresIn,
        refreshTokenExpiresIn: data.tokens.refreshTokenExpiresIn
      });

      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  }

  private async fetchWithAuth<T>(endpoint: string, options: RequestInit = {}, retryCount = 0): Promise<ApiResponse<T>> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new Headers(options.headers);

    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    // Get token from the auth store
    const token = authStore.getAccessToken();

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(url, { ...options, headers });

    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json') && response.status !== 204) {
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      } catch (error) {
        console.error('Error parsing JSON response:', error);
        return { data: null as T, error: 'Failed to parse response' };
      }
    } else {
      // Handle empty responses or non-JSON content
      data = null;
    }

    // If unauthorized and we haven't retried too many times, try to refresh the token
    if (response.status === 401 && retryCount < 1) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Retry the request with the new token
        return this.fetchWithAuth<T>(endpoint, options, retryCount + 1);
      }
    }

    if (!response.ok) {
      return { data: null as T, error: data?.message || 'An error occurred' };
    }

    return { data };
  }

  async getBalances(): Promise<ApiResponse<IBalance[]>> {
    return this.fetchWithAuth<IBalance[]>('/user/balances');
  }

  async getUser(): Promise<ApiResponse<IUserMe[]>> {
    return this.fetchWithAuth<IUserMe[]>('/auth/user');
  }

  async getUserWithdrawableFunds(cryptoCurrencyId: string): Promise<any> {
    return this.fetchWithAuth<any[]>(`/user/withdrawable-funds/${cryptoCurrencyId}`);
  }

  async getProviders(): Promise<ApiResponse<IProvider[]>> {
    return this.fetchWithAuth<IProvider[]>('/game-providers');
  }

  async getDepositBonusOffer(depositType: string): Promise<ApiResponse<IDepositOffer[]>> {
    return this.fetchWithAuth<IDepositOffer[]>(`/user/deposit-offers?depositType=${depositType}`);
  }


  async getSessions(): Promise<ApiResponse<ISessions[]>> {
    return this.fetchWithAuth<ISessions[]>('/user/sessions');
  }

  async getBonusStatistics(): Promise<ApiResponse<IBonusStats>> {
    return this.fetchWithAuth<IBonusStats>('/user/bonuses/statistics');
  }

  async getBonuses(): Promise<ApiResponse<IBonusStats>> {
    return this.fetchWithAuth<IBonusStats>('/bonuses');
  }

  async getActiveBonuses(): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>('/user/bonuses/active');
  }

  async getUserLevels(): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>('/levels');
  }

  async getLevel(): Promise<ApiResponse<ILevelResponse[]>> {
    return this.fetchWithAuth<ILevelResponse[]>('/user/level');
  }

  async getGamblingLimits(): Promise<ApiResponse<IGamblingLimits[]>> {
    return this.fetchWithAuth<IGamblingLimits[]>('/user/gambling-limits');
  }

  async getPromotions(): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.fetchWithAuth<any[]>('/promotions?home=true');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getUserDetails(): Promise<ApiResponse<IUserKyc[]>> {
    return this.fetchWithAuth<IUserKyc[]>('/user-mini-kyc');
  }

  async getUserTotp(): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>('/user-profile/totp');
  }

  async getTournaments(): Promise<ApiResponse<IUserKyc[]>> {
    return this.fetchWithAuth<IUserKyc[]>('/tournaments/active');
  }

  async bankTransfer(data: {amount: string, currency: string, locale: string}): Promise<ApiResponse<IBankTransferResponse[]>> {
    return this.fetchWithAuth<IBankTransferResponse[]>(`/bank-transfer/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async depositAppleGooglePay(data: {amount: string, currency: string}): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/deposits/fiat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async revokeUser(userSessionId:number): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/user/sessions/${userSessionId}/revoke`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userSessionId)
    });
  }

  async setActiveCurrency(currency: string): Promise<ApiResponse<IBalance[]>> {
    return this.fetchWithAuth<IBalance[]>(`/user/crypto-currencies/set-as-active`, { method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({cryptoCurrencyName: currency})
    });
  }

  async redeemBonusCode(bonusCode: string): Promise<ApiResponse<IBalance[]>> {
    return this.fetchWithAuth<IBalance[]>(`/bonuses/redeem-bonus-code`, { method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({bonusCode})
    });
  }

  async forfeitBonus(userBonusId: string): Promise<ApiResponse<IBalance[]>> {
    return this.fetchWithAuth<IBalance[]>(`/user/bonuses/${userBonusId}/forfeit`, { method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });
  }

  async depositOffer(userDepositOfferId: number, type: boolean): Promise<ApiResponse<any[]>> {
    // No date formatting needed in this method, but if there are any date parameters in other methods, update them
    return this.fetchWithAuth<any[]>(`/user/deposit-offers/${userDepositOfferId}/${type? 'activate' : 'deactivate'}`, { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });
  }

  async claimBonusByType(type: 'DEPOSIT' | 'RAKEBACK' | 'SPINNER' | 'CASHBACK'): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/bonuses/by-type/${type}/claim`, { method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });
  }

  async checkBonusAvailability(type: 'DEPOSIT'): Promise<ApiResponse<{enabled: boolean, canToggle: boolean}>> {
    try {
      const response = await this.fetchWithAuth<{enabled: boolean, canToggle: boolean}>(`/bonuses/by-type/${type}/bonus-availability`, { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      return response;
    } catch (error) {
      console.error(`UserService - Error checking bonus availability:`, error);
      throw error;
    }
  }

  async defaultFiatCurrency(currency: 'EUR' | 'USD' | 'GBP' | 'CAD'): Promise<ApiResponse<IBalance[]>> {
    return this.fetchWithAuth<IBalance[]>(`/user/default-fiat-currency`, { method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({name: currency})
    });
  }
  async setGamblingLimits(data: any): Promise<ApiResponse<any[]>> {
    // Format any dates in the data
    if (data.startDate) {
      data.startDate = formatDateForApi(data.startDate);
    }
    if (data.endDate) {
      data.endDate = formatDateForApi(data.endDate);
    }

    return this.fetchWithAuth<any[]>(`/user/gambling-limits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async setTotp(data: any): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/user-profile/totp/set`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async postTotpCodeAndUrl(): Promise<ApiResponse<ITotpCode[]>> {
    return this.fetchWithAuth<ITotpCode[]>(`/user-profile/totp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async setSelfExclusion(data: any): Promise<ApiResponse<any[]>> {
    // Format any dates in the data
    if (data.startDate) {
      data.startDate = formatDateForApi(data.startDate);
    }
    if (data.endDate) {
      data.endDate = formatDateForApi(data.endDate);
    }

    return this.fetchWithAuth<any[]>(`/self-exclusion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async deleteGamblingLimits(data: any): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/user/gambling-limits/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async claimCommission(): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/user/affiliate/claim-available-commissions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async loginMagikLink(token: any): Promise<any> {
    const res = await fetch(`${this.apiUrl}/auth/magic-link`, {
      method: 'POST',
      body: JSON.stringify({token}),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const response = await res.json();

    // If login was successful and we have tokens, update the auth store
    if (response.data && response.data.tokens) {
      authStore.setAuth({
        accessToken: response.data.tokens.accessToken,
        refreshToken: response.data.tokens.refreshToken,
        accessTokenExpiresIn: response.data.tokens.accessTokenExpiresIn,
        refreshTokenExpiresIn: response.data.tokens.refreshTokenExpiresIn
      });
    }

    return response;
  }

  async verifyEmail(data: any): Promise<any> {
    const res = await fetch(`${this.apiUrl}/user-profile/email/confirm`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return await res.json();
  }

  async resetPassword(data: any): Promise<any> {
    const res = await fetch(`${this.apiUrl}/forgot-password/reset`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return await res.json();
  }

  async getReferrals(page = 0): Promise<any> {
    return  await this.fetchWithAuth(`/user/affiliate/referrals?page=${page}&size=${30}`);
  }


  async confirmVerificationCode(verificationCode: any): Promise<any> {
    const res = await fetch(`${this.apiUrl}/forgot-password/confirm-verification-code`, {
      method: 'POST',
      body: JSON.stringify({verificationCode}),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return await res.json();
  }

  async logout(): Promise<any> {
    const response = await this.fetchWithAuth<any[]>(`/user/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });

    // Clear authentication state in the auth store
    authStore.clearAuth();

    return response;
  }

  async initClick(data: any): Promise<any> {
    setLocalStorage('ckt', data.token);
    return this.fetchWithAuth<any[]>(`/ac/t`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async getUserWagers(pageConfig = {page: 0, size:30}): Promise<any> {
    return this.fetchWithAuth<any[]>(`/user/wagers?page=${pageConfig.page}&size=${pageConfig.size}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async getUserWagerById(id: number): Promise<any> {
    return this.fetchWithAuth<any[]>(`/user/wagers/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }


}

export const userService = new UserService();
